import React from 'react';

/**
 * Avatar — a 40px circle with the person's initials on a soft tone (spec §3.5): the tone is a
 * hash of the id over [brand, success, info, purple, coral, teal], so a person keeps their colour
 * everywhere. The signed-in staff member (`me`) is the solid brand yellow with a ring; a photo
 * (`src`) replaces the initials; `presence` adds the 10px status dot.
 *
 *   <Avatar name="Jane Karimova" id={user.id} />
 *   <Avatar name={me.name} me presence="online" size="sm" />
 *
 * Props: name, id (hashed for the tone; defaults to the name), src, size ('sm' 32 | 'md' 40 | 'lg' 56),
 * me, presence ('online' | 'offline'), decorative, className.
 *
 * `decorative` hides the avatar from screen readers: use it whenever the person's name is printed
 * next to it (a table row, a list row, a checkbox label), so the name is not read twice. Without
 * it the avatar is an image named after the person (a lone avatar in a toolbar or a card header).
 *
 * `initialsOf` and `avatarTone` are the shared helpers for the places that draw their own chip:
 * the same person keeps the same initials and tone on every page.
 */

export const AVATAR_TONES = ['brand', 'success', 'info', 'purple', 'coral', 'teal'];

/** "Jane Karimova" -> "JK" · "jane" -> "J" · "" -> "?" */
export function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((p) => Array.from(p)[0]).join('').toUpperCase();
}

/** A stable tone for an id (or any string). */
export function avatarTone(id) {
  const text = String(id ?? '');
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[hash % AVATAR_TONES.length];
}

function Avatar({ name, id, src, size = 'md', me = false, presence, decorative = false, className = '' }) {
  const cls = [
    'ui-avatar',
    size === 'sm' ? 'ui-avatar--sm' : size === 'lg' ? 'ui-avatar--lg' : '',
    me ? 'ui-avatar--me' : '',
    className,
  ].filter(Boolean).join(' ');
  return (
    <span
      className={cls}
      data-tone={me ? undefined : avatarTone(id ?? name)}
      {...(decorative ? { 'aria-hidden': 'true' } : { role: 'img', 'aria-label': name || undefined })}
    >
      {src ? <img src={src} alt="" loading="lazy" /> : initialsOf(name)}
      {presence && <span className={`ui-presence${presence === 'offline' ? ' ui-presence--off' : ''}`} />}
    </span>
  );
}

export default Avatar;
